import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { ArrowDropDownIcon, Text } from 'uikit'

const DropDownHeader = styled.div<{ size: string; color: string }>`
  width: 100%;
  height: ${({ size }) => (size === 'md' ? '35px' : '29px')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ size }) => (size === 'md' ? '0 24px' : '8px 16px')};
  box-shadow: ${({ theme }) => theme.shadows.inset};
  border-radius: 14.5px;
  transition: border-radius 0.15s;
  background: ${({ color, theme }) => (color === 'primary' ? theme.colors.primary : '#052136')};
  margin-top: ${({ size }) => (size === 'md' ? '0' : '12px')};
`

const DropDownListContainer = styled.div<{ size: string; color: string }>`
  min-width: ${({ size }) => (size === 'md' ? '250px' : '136px')};
  height: 0;
  position: absolute;
  overflow: hidden;
  background-color: ${({ color }) => (color === 'primary' ? '#4a008a' : '#052136')};
  z-index: ${({ theme }) => theme.zIndices.dropdown};
  transition: transform 0.15s, opacity 0.15s;
  transform: scaleY(0);
  transform-origin: top;
  opacity: 0;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: ${({ size }) => (size === 'md' ? '250px' : '168px')};
  }
`

const DropDownContainer = styled.div<{ isOpen: boolean; width: number; height: number; size: string; color: string }>`
  cursor: pointer;
  width: ${({ width }) => width}px;
  position: relative;
  background: ${({ theme }) => theme.colors.input};
  border-radius: ${({ size }) => (size === 'md' ? '17.5px' : '14.5px')};
  height: ${({ size }) => (size === 'md' ? '35px' : '29px')};
  min-width: ${({ size }) => (size === 'md' ? '250px' : '136px')};
  user-select: none;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: ${({ size }) => (size === 'md' ? '250px' : '168px')};
  }

  ${(props) =>
    props.isOpen &&
    css`
      ${DropDownHeader} {
        border-bottom: 1px solid ${({ theme }) => (props.color === 'primary' ? 'none' : theme.colors.inputSecondary)};
        box-shadow: ${({ theme }) => (props.color === 'primary' ? 'none' : theme.tooltip.boxShadow)};
        border-radius: ${() => (props.size === 'md' ? '17.5px 17.5px 0 0' : '16px 16px 0 0')};
      }

      ${DropDownListContainer} {
        height: auto;
        transform: scaleY(1);
        opacity: 1;
        border: ${({ theme }) => (props.color === 'primary' ? 'none' : `1px solid ${theme.colors.inputSecondary}`)};
        border-top-width: 0;
        border-radius: 0 0 16px 16px;
        box-shadow: ${({ theme }) => (props.color === 'primary' ? 'none' : theme.tooltip.boxShadow)};
        max-height: 300px;
        overflow: auto;
      }
    `}

  svg {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
  }
`

const DropDownList = styled.ul`
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  z-index: ${({ theme }) => theme.zIndices.dropdown};
`

const ListItem = styled.li<{ size: string; color: string }>`
  list-style: none;
  padding: ${({ size }) => (size === 'md' ? '8.5px 24px' : '8px 16px')};
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
  }
  border-top: ${({ size }) => (size === 'md' ? '1px solid #6001b0' : 'none')};
`

export interface SelectProps {
  options: OptionProps[]
  onChange?: (option: OptionProps) => void
  color?: string
  size?: string
  initIndex?: number
  placeHolder?: string
}

export interface OptionProps {
  label: string
  value: any
}

const Select: React.FunctionComponent<SelectProps> = ({
  options,
  onChange,
  color = 'normal',
  size = 'sm',
  initIndex = 0,
  placeHolder = '',
}) => {
  const containerRef = useRef(null)
  const dropdownRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(initIndex)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  const toggling = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsOpen(!isOpen)
    event.stopPropagation()
  }

  const onOptionClicked = (selectedIndex: number) => () => {
    setSelectedOptionIndex(selectedIndex)
    setIsOpen(false)

    if (onChange) {
      onChange(options[selectedIndex])
    }
  }

  useEffect(() => {
    setSelectedOptionIndex(initIndex)
  }, [initIndex])

  useEffect(() => {
    setContainerSize({
      width: dropdownRef.current.offsetWidth, // Consider border
      height: dropdownRef.current.offsetHeight,
    })

    const handleClickOutside = () => {
      setIsOpen(false)
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  return (
    <DropDownContainer isOpen={isOpen} ref={containerRef} {...containerSize} size={size} color={color}>
      {containerSize.width !== 0 && (
        <DropDownHeader onClick={toggling} color={color} size={size}>
          <Text fontSize={size === 'md' ? '15px' : '10px'}>
            {selectedOptionIndex >= 0 ? options[selectedOptionIndex].label : placeHolder}
          </Text>
        </DropDownHeader>
      )}
      <ArrowDropDownIcon color="text" onClick={toggling} />
      <DropDownListContainer color={color} size={size}>
        <DropDownList ref={dropdownRef}>
          {options.map((option, index) =>
            index !== selectedOptionIndex ? (
              <ListItem onClick={onOptionClicked(index)} key={option.label} color={color} size={size}>
                <Text fontSize={size === 'md' ? '12px' : '10px'}>{option.label}</Text>
              </ListItem>
            ) : null,
          )}
        </DropDownList>
      </DropDownListContainer>
    </DropDownContainer>
  )
}

export default Select
